import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Button from '../common/Button';
import Input from '../common/Input';
import './ProductForm.css'; // Reuse same CSS

const CategoryForm = ({ category, onSubmit, onCancel, loading }) => {
  const [activeLanguage, setActiveLanguage] = useState('ka');
  const [formData, setFormData] = useState({
    // Georgian (required)
    name_ka: '',
    description_ka: '',
    // English (optional)
    name_en: '',
    description_en: '',
    // Russian (optional)
    name_ru: '',
    description_ru: '',
    // Common
    parent_id: '',
    image: null
  });
  const [preview, setPreview] = useState(null);
  const [mainCategories, setMainCategories] = useState([]);

  useEffect(() => {
    fetchMainCategories();
    if (category) {
      setFormData({
        name_ka: category.name_ka || category.name || '',
        name_en: category.name_en || '',
        name_ru: category.name_ru || '',
        description_ka: category.description_ka || category.description || '',
        description_en: category.description_en || '',
        description_ru: category.description_ru || '',
        parent_id: category.parent_id || '',
        image: null
      });
      if (category.image) {
        setPreview(`https://kid-o.ge${category.image}`);
      }
    }
  }, [category]);

  const fetchMainCategories = async () => {
    try {
      const response = await api.get('/admin/categories/main');
      setMainCategories(response.data.data);
    } catch (error) {
      console.error('Error fetching main categories:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate Georgian name (required)
    if (!formData.name_ka.trim()) {
      alert('Georgian category name is required!');
      setActiveLanguage('ka');
      return;
    }
    
    const data = new FormData();
    // Georgian
    data.append('name_ka', formData.name_ka);
    data.append('description_ka', formData.description_ka);
    // English
    data.append('name_en', formData.name_en);
    data.append('description_en', formData.description_en);
    // Russian
    data.append('name_ru', formData.name_ru);
    data.append('description_ru', formData.description_ru);
    // Common
    if (formData.parent_id) {
      data.append('parent_id', formData.parent_id);
    }
    if (formData.image) {
      data.append('image', formData.image);
    }
    
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="form-container">
      {/* Language Tabs */}
      <div className="language-tabs">
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ka' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ka')}
        >
          🇬🇪 Georgian (ქართული) *
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('en')}
        >
          🇬🇧 English
        </button>
        <button
          type="button"
          className={`language-tab ${activeLanguage === 'ru' ? 'active' : ''}`}
          onClick={() => setActiveLanguage('ru')}
        >
          🇷🇺 Russian (Русский)
        </button>
      </div>

      {/* Georgian Fields */}
      {activeLanguage === 'ka' && (
        <div className="language-content">
          <div className="language-note">
            <strong>ქართული</strong> - ძირითადი ენა (სავალდებულო)
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Category Name (Georgian)"
                name="name_ka"
                value={formData.name_ka}
                onChange={handleChange}
                placeholder="მაგ: კაცები, ქალები, ფეხსაცმელი"
                required
              />
            </div>
          </div>
        </div>
      )}

      {/* English Fields */}
      {activeLanguage === 'en' && (
        <div className="language-content">
          <div className="language-note">
            <strong>English</strong> - Optional translation
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Category Name (English)"
                name="name_en"
                value={formData.name_en}
                onChange={handleChange}
                placeholder="e.g., Men, Women, Shoes"
              />
            </div>
          </div>
        </div>
      )}

      {/* Russian Fields */}
      {activeLanguage === 'ru' && (
        <div className="language-content">
          <div className="language-note">
            <strong>Русский</strong> - Необязательный перевод
          </div>
          <div className="form-grid">
            <div className="form-group">
              <Input
                label="Category Name (Russian)"
                name="name_ru"
                value={formData.name_ru}
                onChange={handleChange}
                placeholder="например, Мужчины, Женщины, Обувь"
              />
            </div>
          </div>
        </div>
      )}

      {/* Common Fields */}
      <div className="common-fields">
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
          პარამეტრები (ყველა ენაზე)
        </h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">ზეკატეგორია</label>
            <select
              name="parent_id"
              value={formData.parent_id}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">არცერთი (მთავარი)</option>
              {mainCategories
                .filter(cat => !category || cat.id !== category.id)
                .map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
            </select>
            <small style={{ color: 'var(--gray)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
              Leave empty to create a main category, or select a parent to create a subcategory
            </small>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={loading}>
          {category ? 'Update Category' : 'Create Category'}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;